import { create } from 'zustand';
import { Platform } from 'react-native';
import { fetchWithAuth, getAuthStore } from '@/lib/api/client';
import { loadPluginPackage, pluginRuntime } from './portable.cjs';
import { pluginOwner } from './session';
import { legacyPlugins, markLegacyPluginsSynced } from './deviceStorage';
export { canImportPluginFolder, importPluginFolder } from './deviceStorage';

export const usePluginStore = create(() => ({ revision: 0, userId: null, generation: null, library: null, catalog: [], connections: null }));
let connectionRead = 0;
export function clearPluginCache() {
  connectionRead++;
  usePluginStore.setState(state => ({ userId: null, generation: null, library: null, catalog: [], connections: null, revision: state.revision + 1 }));
}
function assertCurrent(guard) {
  if (!guard.current()) throw new Error('Your account changed. Open Plugins again.');
}
function publish(userId, guard, library, catalog) {
  assertCurrent(guard);
  usePluginStore.setState(state => {
    if (state.userId === userId && state.generation === guard.generation && state.library?.revision > library.revision) return state;
    return { userId, generation: guard.generation, library, catalog: catalog || state.catalog, revision: state.revision + 1 };
  });
}
export function readPluginState(userId) {
  const guard = pluginOwner(userId), state = usePluginStore.getState();
  if (state.userId !== userId || state.generation !== guard.generation || !state.library)
    return { records: [], catalog: [], loaded: false };
  return { ...state.library, catalog: state.catalog, loaded: true,
    mcp: state.connections?.userId === userId && state.connections?.generation === guard.generation ? state.connections.data : undefined };
}
export function installedPlugins(userId) { return readPluginState(userId).records; }
async function save(userId, guard, library, records, catalog) {
  assertCurrent(guard);
  const saved = await fetchWithAuth('/json/plugins', { method: 'POST', networkRetries: 0,
    body: { revision: library.revision, records }, isRequestCurrent: guard.current });
  assertCurrent(guard);
  if (saved.error) throw new Error(saved.message || 'Could not sync plugins.');
  publish(userId, guard, saved, catalog);
  return saved;
}
export async function refreshPlugins(userId) {
  const guard = pluginOwner(userId);
  let data;
  try {
    data = await fetchWithAuth('/json/plugins', { isRequestCurrent: guard.current });
  } catch (error) {
    assertCurrent(guard);
    // Older production servers have no plugin library route. Keep this
    // separate from migration/save failures and never publish an empty cache.
    if (error.status === 404) throw Object.assign(new Error('Plugins are not available on the server yet. Please try again after the server update.'), { code: 'PLUGIN_LIBRARY_UNAVAILABLE' });
    throw error;
  }
  assertCurrent(guard);
  if (data.protocol !== 2 || !Array.isArray(data.catalog) || !Array.isArray(data.records))
    throw new Error('The server needs an update to sync plugins.');
  let library = { revision: data.revision, records: data.records };
  const legacy = legacyPlugins(userId);
  if (legacy.length) {
    const records = [...library.records];
    for (const record of legacy) {
      if (records.some(item => item.id === record.id)) continue; // Account choices win.
      const pkg = record.source === 'bundled' ? data.catalog.find(entry => `bundled:${entry.id}` === record.id)?.package : record.package;
      if (!pkg) throw new Error('A saved Mac plugin is unavailable. Your original plugin list has been preserved.');
      records.push({ ...record, package: loadPluginPackage(pkg), enabled: record.enabled === true });
    }
    library = await save(userId, guard, library, records, data.catalog);
    assertCurrent(guard);
    markLegacyPluginsSynced(userId);
  }
  publish(userId, guard, library, data.catalog);
  return { ...library, catalog: data.catalog };
}
export async function installPlugin(userId, pkg, source = 'local') {
  const guard = pluginOwner(userId), loaded = loadPluginPackage(pkg);
  if (!loaded.skills.length && !Object.keys(loaded.mcpServers || {}).length) throw new Error('This plugin has no supported skills or MCP configuration.');
  const library = await refreshPlugins(userId);
  assertCurrent(guard);
  const id = `${source === 'bundled' ? 'bundled' : 'local'}:${loaded.manifest.name}`;
  const records = library.records.filter(record => record.id !== id);
  return save(userId, guard, library, [...records, { id, source: source === 'bundled' ? 'bundled' : 'local', enabled: true, package: loaded }]);
}
export async function updatePlugin(userId, id, enabled) {
  const guard = pluginOwner(userId), library = await refreshPlugins(userId);
  assertCurrent(guard);
  if (!library.records.some(record => record.id === id)) throw new Error('This plugin was removed on another device.');
  return save(userId, guard, library, enabled === null ? library.records.filter(record => record.id !== id)
    : library.records.map(record => record.id === id ? { ...record, enabled } : record));
}
export async function preparePluginLaunch(desktop) {
  if (Platform.OS !== 'macos') return undefined;
  const userId = getAuthStore().getState().user?.id;
  if (!userId) return undefined;
  const guard = pluginOwner(userId);
  let library;
  try {
    library = await refreshPlugins(userId);
  } catch (error) {
    assertCurrent(guard);
    if (error.code !== 'PLUGIN_LIBRARY_UNAVAILABLE') throw error;
    const known = [...installedPlugins(userId), ...legacyPlugins(userId)];
    if (known.some(record => record.enabled)) throw error;
    // No plugin payload for legacy Work servers. Keep the account guard, and
    // probe again on the next launch so a backend deploy needs no app restart.
    return { isCurrent: guard.current };
  }
  assertCurrent(guard);
  const enabled = library.records.filter(record => record.enabled);
  const needsBrowser = enabled.some(record => pluginRuntime(record.package) === 'mac-browser');
  if (needsBrowser && Platform.OS === 'macos' && !desktop) throw new Error('Set up Browser and app access in Plugins to use your enabled browser plugins, or turn them off.');
  if (needsBrowser && desktop && !desktop.agentPluginsSupported) throw new Error('The server needs an update to use your browser plugins.');
  return { installedPluginIds: enabled.filter(record => pluginRuntime(record.package) === 'work' || desktop?.agentPluginsSupported)
    .map(record => record.id), isCurrent: guard.current };
}

export async function refreshPluginConnections(userId) {
  const guard = pluginOwner(userId), sequence = ++connectionRead;
  const data = await fetchWithAuth('/json/plugins/connections', { isRequestCurrent: guard.current });
  assertCurrent(guard);
  if (!Array.isArray(data.connections) || !Array.isArray(data.servers)) throw new Error('The server needs an update to connect plugin accounts.');
  if (sequence === connectionRead) usePluginStore.setState(state => ({ connections: { userId, generation: guard.generation, data }, revision: state.revision + 1 }));
  return data;
}
export async function beginPluginConnection(userId, pluginId, server) {
  const guard = pluginOwner(userId);
  const data = await fetchWithAuth('/json/plugins/connections/start', { method: 'POST', networkRetries: 0,
    body: { pluginId, server }, isRequestCurrent: guard.current });
  assertCurrent(guard);
  const url = new URL(data.url);
  if (url.protocol !== 'https:' || url.pathname !== '/json/plugins/oauth/consent') throw new Error('Invalid account connection link.');
  return { url: url.href, isCurrent: guard.current };
}
export async function disconnectPluginConnection(userId, server) {
  const guard = pluginOwner(userId);
  await fetchWithAuth('/json/plugins/connections/disconnect', { method: 'POST', networkRetries: 0,
    body: { server }, isRequestCurrent: guard.current });
  assertCurrent(guard);
  return refreshPluginConnections(userId);
}
